import { z } from "zod/v4"
import { ShopBannerId, ShopCustomTagId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { UpdatedAt } from "./system"

/**
 * ショップTOP編成のFirst表示用アイテム
 */
export const ShopTopCompositionFirstItem = z.object({
  id: z.string().describe("バナーID"),
  priority: z.int().min(1).max(99999).describe("優先度（数値が大きいほど上位に表示される）"),
})

export type ShopTopCompositionFirstItem = z.infer<typeof ShopTopCompositionFirstItem>

/**
 * ショップTOP編成のFirst表示用ドキュメント
 */
export const ShopTopCompositionFirstDocumentBody = z.object({
  items: z.array(ShopTopCompositionFirstItem).describe("First表示用バナーの一覧"),
  updated_at: UpdatedAt,
})

export type ShopTopCompositionFirstDocumentBody = z.infer<typeof ShopTopCompositionFirstDocumentBody>

export type ShopTopCompositionFirstDocument = ShopTopCompositionFirstDocumentBody

export class ShopTopCompositionFirstDocuments {
  static toDocumentData(doc: ShopTopCompositionFirstDocument): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ShopTopCompositionFirstDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      items: data["items"] ?? [],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * ショップTOP編成のList表示用アイテムの種類
 */
export const ShopTopCompositionListItemKind = z.enum(["banner", "customTag"]).describe("アイテムの種類")
export type ShopTopCompositionListItemKind = z.infer<typeof ShopTopCompositionListItemKind>

/**
 * ショップTOP編成のList表示用アイテム
 */
export const ShopTopCompositionListItem = z.object({
  kind: ShopTopCompositionListItemKind.describe("アイテムの種類（banner=ショップバナー、customTag=カスタムタグ）"),
  id: z.string().describe("アイテムのID（kindに応じてShopBannerIdまたはShopCustomTagId）"),
  priority: z.int().min(1).max(99999).describe("優先度（数値が大きいほど上位に表示される）"),
})

export type ShopTopCompositionListItem = z.infer<typeof ShopTopCompositionListItem>

/**
 * ショップTOP編成のList表示用ドキュメント
 */
export const ShopTopCompositionListDocumentBody = z.object({
  items: z.array(ShopTopCompositionListItem).describe("List表示用アイテムの一覧"),
  updated_at: UpdatedAt,
})

export type ShopTopCompositionListDocumentBody = z.infer<typeof ShopTopCompositionListDocumentBody>

export type ShopTopCompositionListDocument = ShopTopCompositionListDocumentBody

export class ShopTopCompositionListDocuments {
  static toDocumentData(doc: ShopTopCompositionListDocument): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): ShopTopCompositionListDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      items: data["items"] ?? [],
      updated_at: data["updated_at"],
    }
  }
}

/**
 * ショップTOP編成のドキュメントID
 */
export const ShopTopCompositionDocumentIds = {
  first: "first",
  list: "list",
} as const

/**
 * リクエストバリデーション用のZodスキーマ
 */
export const UpdateShopTopCompositionFirstRequest = z.object({
  items: z.array(z.object({
    id: z.string().describe("バナーID"),
    priority: z.int().min(1).max(99999).describe("優先度"),
  })).describe("更新するバナーの一覧（優先度は一意である必要がある）"),
  updatedAt: z.iso.datetime().describe("編集元データの最終更新日時（楽観的ロック用）"),
})

export type UpdateShopTopCompositionFirstRequest = z.infer<typeof UpdateShopTopCompositionFirstRequest>

export const UpdateShopTopCompositionListRequest = z.object({
  items: z.array(z.object({
    kind: ShopTopCompositionListItemKind.describe("アイテムの種類"),
    id: z.string().describe("アイテムのID"),
    priority: z.int().min(1).max(99999).describe("優先度"),
  })).describe("更新するアイテムの一覧（優先度は一意である必要がある）"),
  updatedAt: z.iso.datetime().describe("編集元データの最終更新日時（楽観的ロック用）"),
})

export type UpdateShopTopCompositionListRequest = z.infer<typeof UpdateShopTopCompositionListRequest>

/**
 * 優先度の重複チェック
 */
export function validatePriorityUniqueness<T extends { priority: number }>(items: T[]): boolean {
  const priorities = items.map(item => item.priority)
  return new Set(priorities).size === priorities.length
}

/**
 * バナーIDの存在チェック用ヘルパー
 */
export function extractBannerIds(items: ShopTopCompositionFirstItem[]): ShopBannerId[] {
  return items.map(item => ShopBannerId(item.id))
}

/**
 * List表示用アイテムからバナーIDとカスタムタグIDを抽出
 */
export function extractListItemIds(items: ShopTopCompositionListItem[]): {
  bannerIds: ShopBannerId[]
  customTagIds: ShopCustomTagId[]
} {
  const bannerIds: ShopBannerId[] = []
  const customTagIds: ShopCustomTagId[] = []

  for (const item of items) {
    if (item.kind === "banner") {
      bannerIds.push(ShopBannerId(item.id))
    } else {
      customTagIds.push(ShopCustomTagId(item.id))
    }
  }

  return { bannerIds, customTagIds }
}
